import type { QueryClient } from "@tanstack/react-query";

import { patientStore } from "@src/features/patients";
import { appStore } from "@src/shared/stores/app";
import { removeCsrfToken } from "@src/shared/utils/auth/csrf";
import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";
import * as identify from "@src/shared/utils/observability/identify";

import { signOut } from "../data";

function clearLogoutState(queryClient: QueryClient) {
  queryClient.clear();
  removeCsrfToken();
  patientStore.getState().setPatientId(null);
  appStore.getState().setPracticeTimezone(undefined);
  identify.clearUser();
}

export async function logout(queryClient: QueryClient) {
  let signOutSucceeded = false;

  clearLogoutState(queryClient);

  try {
    const result = await signOut();

    if (result.error) {
      console.error("Better-auth signOut failed:", result.error);
      events.appError.track({
        surface: "logout",
        error: "Better Auth signOut returned an error response",
        error_code: "auth.logout_failed",
        error_source: "logout",
        fatal: false,
      });
    } else {
      signOutSucceeded = true;
    }
  } catch (error) {
    console.error("Better-auth signOut failed:", error);
    events.appError.track({
      surface: "logout",
      error: toErrorMessage(error, "Better Auth signOut threw"),
      error_code: "auth.logout_failed",
      error_source: "logout",
      fatal: false,
    });
  } finally {
    clearLogoutState(queryClient);
  }

  events.userLoggedOut.track({
    reason: signOutSucceeded ? "manual" : "auth_error",
  });
}

export async function logoutAndReloadToLogin(queryClient: QueryClient) {
  await logout(queryClient);
  window.location.assign("/login");
}
